import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

export const fetchQuizByModule = createAsyncThunk(
  "quiz/fetchByModule",
  async (moduleId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/quiz/module/${moduleId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const fetchContenuQuizByQuiz = createAsyncThunk(
  "quiz/fetchContenuQuiz",
  async (quizId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/contenuQuiz/quiz/${quizId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement questions");
    }
  }
);

export const createQuiz = createAsyncThunk(
  "quiz/create",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post("/quiz", formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur création");
    }
  }
);

export const updateQuiz = createAsyncThunk(
  "quiz/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(`/quiz/${id}`, formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur modification");
    }
  }
);

export const deleteQuiz = createAsyncThunk(
  "quiz/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/quiz/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur suppression");
    }
  }
);

export const createContenuQuiz = createAsyncThunk(
  "quiz/createQuestion",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post("/contenuQuiz", formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur création question");
    }
  }
);

export const updateContenuQuiz = createAsyncThunk(
  "quiz/updateQuestion",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(`/contenuQuiz/${id}`, formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur modification question");
    }
  }
);

export const deleteContenuQuiz = createAsyncThunk(
  "quiz/deleteQuestion",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/contenuQuiz/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur suppression question");
    }
  }
);

export const submitQuiz = createAsyncThunk(
  "quiz/submit",
  async ({ quizId, answers, apprenantId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/quiz/${quizId}/submit`, {
        answers,
        apprenantId,   // ← add this
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur soumission quiz");
    }
  }
);

const quizSlice = createSlice({
  name: "quiz",
  initialState: {
    current:    null,   // current quiz
    questions:  [],     // contenuQuiz list
    result:     null,   // score result after submit
    loading:    false,
    error:      null,
    success:    null,
  },
  reducers: {
    clearQuizMessages: (state) => { state.error = null; state.success = null; },
    clearQuizResult:   (state) => { state.result = null; },
  },
  extraReducers: (builder) => {
    builder
      // fetchQuizByModule
      .addCase(fetchQuizByModule.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchQuizByModule.fulfilled, (state, action) => { state.loading = false; state.current = action.payload; })
      .addCase(fetchQuizByModule.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      // fetchContenuQuizByQuiz
      .addCase(fetchContenuQuizByQuiz.fulfilled, (state, action) => {
        state.questions = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
      })

      // createQuiz
      .addCase(createQuiz.fulfilled, (state, action) => {
        state.current = action.payload;
        state.success = "Quiz créé avec succès.";
      })
      .addCase(createQuiz.rejected, (state, action) => { state.error = action.payload; })

      // updateQuiz
      .addCase(updateQuiz.fulfilled, (state, action) => {
        state.current = action.payload;
        state.success = "Quiz mis à jour.";
      })
      .addCase(updateQuiz.rejected, (state, action) => { state.error = action.payload; })

      // deleteQuiz
      .addCase(deleteQuiz.fulfilled, (state) => {
        state.current   = null;
        state.questions = [];
        state.success   = "Quiz supprimé.";
      })
      .addCase(deleteQuiz.rejected, (state, action) => { state.error = action.payload; })

      // createContenuQuiz
      .addCase(createContenuQuiz.fulfilled, (state, action) => {
        state.questions.push(action.payload);
        state.success = "Question ajoutée.";
      })
      .addCase(createContenuQuiz.rejected, (state, action) => { state.error = action.payload; })

      // updateContenuQuiz
      .addCase(updateContenuQuiz.fulfilled, (state, action) => {
        const idx = state.questions.findIndex((q) => q._id === action.payload._id);
        if (idx !== -1) state.questions[idx] = action.payload;
        state.success = "Question mise à jour.";
      })
      .addCase(updateContenuQuiz.rejected, (state, action) => { state.error = action.payload; })

      // deleteContenuQuiz
      .addCase(deleteContenuQuiz.fulfilled, (state, action) => {
        state.questions = state.questions.filter((q) => q._id !== action.payload);
        state.success   = "Question supprimée.";
      })
      .addCase(deleteContenuQuiz.rejected, (state, action) => { state.error = action.payload; })

      // submitQuiz
      .addCase(submitQuiz.pending,   (state) => { state.loading = true; })
      .addCase(submitQuiz.fulfilled, (state, action) => {
        state.loading = false;
        state.result  = action.payload;
        state.success = action.payload.reussi ? "Félicitations! Quiz réussi 🎉" : "Quiz échoué. Réessayez.";
      })
      .addCase(submitQuiz.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { clearQuizMessages, clearQuizResult } = quizSlice.actions;
export default quizSlice.reducer;
